import React, { useEffect, useState } from 'react';
import {
  Device,
  EnergySwitchPanel,
  useEnergySwitchPanelDetailQuery,
  useEnergySwitchPanelDetailUpdatesSubscription,
} from '../../../../types/generated-types';
import DeviceLoadingSkeleton from '../shared-ui/deviceLoadingSkeleton';
import { formatDeviceMetric } from '../../../utils/formatters';
import { DeviceInformation } from '../shared-ui/device-info';
import { Box, Grid, Typography, Paper } from '@mui/material';
import moment from 'moment';
import { updateCacheFromSubscriptionEvent } from '../../../../helpers/subscriptionUtils';
import { EnergySwitchPanelCircuitComponent } from './energy-switch-panel-circuit';

interface EnergySwitchPanelProps {
  id: string;
  onInfoUpdate?: (info: DeviceInformation) => void;
}

interface CircuitInfo {
  id: string;
  info: DeviceInformation;
}

export const EnergySwitchPanelComponent: React.FC<EnergySwitchPanelProps> = ({
  id,
  onInfoUpdate,
}) => {
  const [device, setDevice] = useState<Partial<EnergySwitchPanel>>();
  const [deviceInfo, setDeviceInfo] = useState<DeviceInformation>();
  const [circuitInfos, setCircuitInfos] = useState<
    Record<string, DeviceInformation>
  >({});

  const { data } = useEnergySwitchPanelDetailQuery({
    variables: { id },
  });

  useEnergySwitchPanelDetailUpdatesSubscription({
    variables: { id },
    fetchPolicy: 'no-cache',
    onData: updateCacheFromSubscriptionEvent,
  });

  useEffect(() => {
    if (data?.energySwitchPanelById) {
      setDevice(data.energySwitchPanelById as Partial<EnergySwitchPanel>);
    }
  }, [data]);

  useEffect(() => {
    if (device) {
      // Flatten circuit information into a single level
      const flattenedCircuitInfo = Object.entries(circuitInfos).reduce(
        (acc, [circuitId, circuitInfo]) => {
          Object.entries(circuitInfo).forEach(([section, sectionInfo]) => {
            if (typeof sectionInfo === 'object') {
              Object.entries(sectionInfo).forEach(([key, value]) => {
                acc[`Circuit ${circuitId} - ${section} - ${key}`] = value;
              });
            } else {
              acc[`Circuit ${circuitId} - ${section}`] = sectionInfo;
            }
          });
          return acc;
        },
        {} as Record<string, string>,
      );

      const info: DeviceInformation = {
        'Panel Information': {
          'Panel Name': device.name ?? 'Unnamed Panel',
          'Panel ID': device.deviceId ?? 'Unidentified',
          'Panel Type': device.typeDisplayName ?? 'Unknown',
          'Last Updated': moment(device.timestamp).fromNow(),
        },
        'Power Metrics': {
          'Power Input': formatDeviceMetric({
            value: device.powerInput?.value ?? undefined,
            units: device.powerInput?.units ?? undefined,
          }),
        },
        'System Status': {
          'Power State': device.powerState?.value ? 'On' : 'Off',
          'Is Powered On': device.isPoweredOn ? 'Yes' : 'No',
          'Is Configured': device.isConfigured ? 'Yes' : 'No',
          'Is Controlled': device.isControlled ? 'Yes' : 'No',
          'Has Pending Updates': device.hasPendingUpdates ? 'Yes' : 'No',
        },
        ...flattenedCircuitInfo,
      };
      setDeviceInfo(info);
      onInfoUpdate?.(info);
    }
  }, [device, circuitInfos, onInfoUpdate]);

  const handleCircuitInfoUpdate = (
    circuitId: string,
    info: DeviceInformation,
  ) => {
    setCircuitInfos((prev) => ({
      ...prev,
      [circuitId]: info,
    }));
  };

  if (!device) {
    return <DeviceLoadingSkeleton size="large" />;
  }

  return device ? (
    <Paper elevation={2} sx={{ p: 2, m: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h5" gutterBottom>
            {device.name}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" color="textSecondary" align="center">
            Power Input
          </Typography>
          <Typography variant="h6" align="center">
            {formatDeviceMetric({
              value: device.powerInput?.value ?? undefined,
              units: device.powerInput?.units ?? undefined,
            })}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography variant="subtitle2" color="textSecondary" align="center">
            Status
          </Typography>
          <Typography variant="h6" align="center">
            {device.powerState?.value ? 'On' : 'Off'}
          </Typography>
        </Grid>
        {device.dependentDeviceIds?.map(
          (circuitId) =>
            circuitId && (
              <Grid item xs={12} key={circuitId}>
                <EnergySwitchPanelCircuitComponent
                  id={circuitId}
                  onInfoUpdate={(info) =>
                    handleCircuitInfoUpdate(circuitId, info)
                  }
                />
              </Grid>
            ),
        )}
      </Grid>
    </Paper>
  ) : null;
};
